import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Mail, Send, Loader2, Paperclip, Globe, MapPin, ExternalLink, CheckCircle2,
  ListChecks, AlertTriangle, Clock, FileText,
} from "lucide-react";
import { toast } from "sonner";

const METHOD_STYLE = {
  Email: { cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: Mail, label: "Email submission" },
  Portal: { cls: "bg-purple-50 text-purple-700 border-purple-200", Icon: Globe, label: "Portal submission" },
  Mail: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: MapPin, label: "Mail / physical" },
  Unspecified: { cls: "bg-zinc-50 text-zinc-600 border-zinc-200", Icon: Mail, label: "Unspecified (defaults to Email)" },
};

export function SubmissionMethod({ deal, reload }) {
  const { user } = useAuth();
  const canSubmit = !!user;  // Dir 65 — roles flattened: any authenticated employee can submit (machine-vs-human send gate still applies server-side).
  const sm = deal.submission_method;
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  if (!sm || !sm.method) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-200 p-3 mb-4 text-xs text-zinc-400" data-testid="submission-method-empty">
        Anvil determines the submission method (Email / Portal / Mail) at Stage 7.
      </div>
    );
  }
  const method = sm.method;
  const eff = method === "Unspecified" ? "Email" : method;
  const st = METHOD_STYLE[method] || METHOD_STYLE.Unspecified;
  const Icon = st.Icon;
  const stage7 = (deal.bid_stages || []).find((s) => s.number === "7");
  const done = stage7?.status === "COMPLETE";
  const needsConfirm = eff === "Portal" || eff === "Mail";
  const steps = sm.instructions || [];

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/bids/${deal.id}/confirm-submission`, { note });
      toast.success("Submission confirmed — Stage 7 complete.");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm submission."); }
    finally { setConfirming(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 p-4 mb-4" data-testid="submission-method">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md border ${st.cls}`} data-testid="submission-method-badge">
            <Icon className="h-3.5 w-3.5" /> {st.label}
          </span>
          {done
            ? <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Submitted</span>
            : <span className="flex items-center gap-1 text-[11px] text-orange-600 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Action required</span>}
        </div>
        {sm.deadline && <span className="flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3.5 w-3.5" /> {sm.deadline}</span>}
      </div>

      {sm.notes && <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{sm.notes}</p>}

      {eff === "Email" && (
        <div className="mt-3 text-xs text-zinc-600 space-y-1">
          {sm.email_to && <div><span className="text-zinc-400">Submit to:</span> <span className="font-mono text-zinc-900">{sm.email_to}</span></div>}
          {sm.email_subject_format && <div><span className="text-zinc-400">Subject:</span> {sm.email_subject_format}</div>}
          {sm.format_requirements && <div><span className="text-zinc-400">Format:</span> {sm.format_requirements}</div>}
          <div className="text-[11px] text-zinc-400 pt-1">Use "Submit via Gmail" below — sending completes Stage 7.</div>
        </div>
      )}

      {eff === "Portal" && (
        <div className="mt-3 space-y-3">
          {sm.portal_name && (
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-zinc-700"><span className="text-zinc-400">Portal:</span> <strong>{sm.portal_name}</strong></div>
              {sm.portal_url && (
                <a href={sm.portal_url} target="_blank" rel="noreferrer" data-testid="portal-link"
                  className="flex items-center gap-1 text-[11px] bg-purple-600 text-white px-2.5 py-1 rounded-md hover:bg-purple-500">
                  <ExternalLink className="h-3.5 w-3.5" /> Open portal
                </a>
              )}
            </div>
          )}
          {steps.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5"><ListChecks className="h-3.5 w-3.5" /> Submission steps</div>
              <ol className="space-y-1.5" data-testid="portal-steps">
                {steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-700">
                    <span className="shrink-0 h-4 w-4 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {sm.confirmation && <p className="text-[11px] text-zinc-500">Confirmation: {sm.confirmation}</p>}
        </div>
      )}

      {eff === "Mail" && (
        <div className="mt-3 text-xs text-zinc-600 space-y-1">
          {sm.notes ? null : <div className="text-zinc-400">Mailing instructions:</div>}
          {steps.length > 0 && (
            <ol className="space-y-1.5 mt-1" data-testid="mail-steps">
              {steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2"><span className="shrink-0 h-4 w-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span><span>{step}</span></li>
              ))}
            </ol>
          )}
        </div>
      )}

      {needsConfirm && !done && canSubmit && (
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <div className="text-[11px] text-zinc-500 mb-1.5">Ironclad never auto-submits to a portal. Submit it yourself using the steps above, then confirm:</div>
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="confirm-submission-note"
            placeholder="Confirmation # or note (optional)" className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-xs mb-2 outline-none focus:border-gold-dark" />
          <button onClick={confirm} disabled={confirming} data-testid="confirm-submission-btn"
            className="flex items-center gap-1.5 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-md hover:bg-emerald-500 disabled:opacity-50">
            {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Mark {eff.toLowerCase()} submission complete
          </button>
        </div>
      )}
    </div>
  );
}

export function EmailSubmission({ deal, reload }) {
  const { user } = useAuth();
  const log = deal.email_log || [];
  const canSubmit = !!user;  // Dir 65 — roles flattened: any authenticated employee can submit (machine-vs-human send gate still applies server-side).
  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [capBusy, setCapBusy] = useState(false);
  const [sendConfirm, setSendConfirm] = useState("");

  const attachCapStatement = async () => {
    setCapBusy(true);
    try {
      await api.post(`/bids/${deal.id}/documents/capability-statement`);
      toast.success("Capability Statement generated & attached to this deal.");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't generate the capability statement."); }
    finally { setCapBusy(false); }
  };

  const openCompose = async () => {
    try {
      const r = await api.get(`/bids/${deal.id}/submission-draft`);
      if (!r.data.configured) { toast.error("Gmail isn't configured on the server yet."); return; }
      if (!r.data.connected) { toast.error("Connect your Gmail in Settings first."); return; }
      const to = deal.submission_method?.email_to || r.data.to;
      setDraft({ ...r.data, to, cc: (r.data.cc || []).join(", "), attach_pdf: r.data.proposal_available });
      setSendConfirm("");
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare the email"); }
  };

  const send = async () => {
    if (!draft.to.trim()) { toast.error("A recipient (To) is required."); return; }
    if (sendConfirm.trim() !== "SEND") { toast.error('Type SEND to confirm this external submission.'); return; }
    setSending(true);
    try {
      const payload = {
        to: draft.to.trim(),
        cc: draft.cc.split(",").map((s) => s.trim()).filter(Boolean),
        subject: draft.subject,
        body: draft.body,
        attach_pdf: !!draft.attach_pdf,
        confirm: sendConfirm.trim(),
      };
      const r = await api.post(`/bids/${deal.id}/submit-via-gmail`, payload);
      toast.success(`Bid submitted to ${r.data.to}`);
      setOpen(false); setDraft(null); setSendConfirm(""); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="email-submission">
      <SubmissionMethod deal={deal} reload={reload} />
      {deal.estimate_confidence?.available && !deal.estimate_confidence.bid_ready && (
        <div className="mb-3 border border-red-300 bg-red-50 rounded-md px-3 py-2 text-[12px] text-red-800 flex gap-2" data-testid="presend-confidence-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div><b>NOT bid-ready — needs measured takeoff.</b> Only {deal.estimate_confidence.backed_pct}% of the priced dollars trace to published/measured quantities ({deal.estimate_confidence.label}). Confirm or measure the flagged quantities before sending.</div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-zinc-900 font-mono flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email tracking & submission</div>
        <div className="flex items-center gap-2">
          {canSubmit && (
            <button onClick={attachCapStatement} disabled={capBusy} data-testid="attach-cap-statement-btn"
              className="flex items-center gap-1.5 text-xs border border-zinc-300 text-zinc-700 px-3 py-1.5 rounded-md hover:bg-zinc-50 disabled:opacity-50">
              {capBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 text-gold-dark" />} Capability Statement
            </button>
          )}
          {canSubmit && (
            <button onClick={openCompose} data-testid="submit-via-gmail-btn"
              className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800">
              <Send className="h-3.5 w-3.5" /> Submit via Gmail
            </button>
          )}
        </div>
      </div>
      {log.length === 0 && <div className="text-xs text-zinc-400">No emails yet. Tracking codes appear here once a payroll or submission email is prepared/sent.</div>}
      <div className="space-y-2">
        {log.slice().reverse().map((e, i) => (
          <div key={i} className="border border-zinc-200 rounded-md p-2.5" data-testid={`email-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-900">{e.subject}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${["opened", "clicked"].includes(e.status) ? "bg-emerald-100 text-emerald-700" : e.status === "delivered" ? "bg-blue-100 text-blue-600" : e.status === "sent" ? "bg-zinc-100 text-zinc-600" : ["error", "bounced", "complained"].includes(e.status) ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{e.status}</span>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{e.tracking_code} · {e.type === "submission" ? "submission" : "payroll"} · to {e.to}{e.from ? ` · from ${e.from}` : ""}</div>
            {e.attachment && <div className="text-[10px] text-zinc-400 mt-0.5">📎 {e.attachment}</div>}
            {e.error && <div className="text-[10px] text-red-500 mt-1">{e.error}</div>}
            {e.missing?.length > 0 && <div className="text-[10px] text-red-500 mt-1">MISSING: {e.missing.join("; ")}</div>}
          </div>
        ))}
      </div>

      {open && draft && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="gmail-compose-modal" onClick={() => !sending && setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Send className="h-4 w-4" /> Submit bid via Gmail</div>
              <span className="text-[10px] text-zinc-500 font-mono">from {draft.connected_email}</span>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">To</label>
                <input value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} data-testid="compose-to"
                  placeholder="contracting.officer@agency.gov" className="w-full mt-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm text-zinc-900 outline-none focus:border-gold-dark" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">CC</label>
                <input value={draft.cc} onChange={(e) => setDraft({ ...draft, cc: e.target.value })} data-testid="compose-cc"
                  className="w-full mt-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm text-zinc-900 outline-none focus:border-gold-dark" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Subject</label>
                <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="compose-subject"
                  className="w-full mt-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm text-zinc-900 outline-none focus:border-gold-dark" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Message</label>
                <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="compose-body" rows={7}
                  className="w-full mt-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm text-zinc-900 outline-none focus:border-gold-dark font-mono leading-relaxed" />
                <div className="text-[10px] text-zinc-400 mt-1">Your Gmail signature is appended automatically.</div>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-700" data-testid="compose-attach">
                <input type="checkbox" checked={!!draft.attach_pdf} disabled={!draft.proposal_available}
                  onChange={(e) => setDraft({ ...draft, attach_pdf: e.target.checked })} />
                <Paperclip className="h-3.5 w-3.5" /> Attach proposal PDF
                {!draft.proposal_available && <span className="text-[10px] text-amber-600">(no proposal drafted yet)</span>}
              </label>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-zinc-600 whitespace-nowrap">Type <span className="font-bold tracking-wider">SEND</span> to confirm</label>
                <input value={sendConfirm} onChange={(e) => setSendConfirm(e.target.value)} data-testid="compose-send-confirm"
                  placeholder="SEND" autoComplete="off"
                  className="w-24 border border-zinc-300 rounded px-2 py-1.5 text-xs font-mono tracking-widest uppercase outline-none focus:border-gold-dark" />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setOpen(false)} disabled={sending} className="text-xs text-zinc-500 hover:text-zinc-800 px-3 py-1.5">Cancel</button>
                <button onClick={send} disabled={sending || sendConfirm.trim() !== "SEND"} data-testid="compose-send"
                  title={sendConfirm.trim() !== "SEND" ? "Type SEND to confirm this external submission" : ""}
                  className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-4 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed">
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
